<script setup lang="ts">
import { NButton } from 'naive-ui'

// First-run experience for an empty brain. Replaces the one-line
// "No wiki index yet" welcome with a structured card that explains what
// superbrain is and shows concrete first steps — example ingests and
// example questions — plus a primary CTA into the Add-to-wiki drawer.
// Rendered only at zero pages (HomeView's `welcomeVisible`), so it
// disappears the moment the first ingest lands.
//
// The examples are illustrative copy, not interactive shortcuts — the
// goal is to teach the shape of a good first input (a concrete statement
// or a URL, not a vague pointer) and a good first question.

defineEmits<{ (e: 'add-to-wiki'): void }>()

const exampleIngests = [
  { label: 'A bio URL', hint: 'your LinkedIn / personal site — “https://…”' },
  { label: 'A project brief', hint: '“Project Atlas is a Rails app that…”' },
  { label: 'A reading-list article', hint: 'paste the URL of something you just read' },
]

const exampleQuestions = [
  'What am I working on right now?',
  'What did I learn about prompt caching?',
  'Who have I taken notes on?',
]
</script>

<template>
  <section class="pt-8 max-w-xl flex flex-col gap-6" data-testid="empty-wiki-onboarding">
    <header class="flex flex-col gap-2">
      <h2 class="text-xl font-semibold text-white m-0">Welcome to your superbrain</h2>
      <p class="text-white/60 leading-relaxed m-0">
        A wiki that builds itself. Feed it notes, URLs, and documents; it distils them into linked pages you can ask
        questions against. It's empty right now — add a few things and it starts compounding.
      </p>
    </header>

    <div class="flex flex-col gap-2">
      <h3 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">Start by adding</h3>
      <ul class="list-none p-0 m-0 flex flex-col gap-2">
        <li v-for="ex in exampleIngests" :key="ex.label" data-testid="example-ingest" class="text-sm text-white/80">
          <span class="text-white">{{ ex.label }}</span>
          <span class="text-white/40"> — {{ ex.hint }}</span>
        </li>
      </ul>
    </div>

    <div class="flex flex-col gap-2">
      <h3 class="text-xs font-semibold uppercase tracking-widest text-white/40 m-0">Then ask things like</h3>
      <ul class="list-none p-0 m-0 flex flex-col gap-1">
        <li v-for="q in exampleQuestions" :key="q" data-testid="example-question" class="text-sm text-white/60 italic">
          “{{ q }}”
        </li>
      </ul>
    </div>

    <div>
      <NButton type="primary" data-testid="onboarding-cta" @click="$emit('add-to-wiki')"> Add to brain </NButton>
    </div>
  </section>
</template>
